import { X } from 'lucide-react';
import { Dialog as DialogPrimitive, DropdownMenu, Tabs as TabsPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from './button.js';
import { FormField } from './form-field.js';
import { Input } from './input.js';

// ───────── Tooltip ─────────

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
}

/** Supplementary info only — never put essential content exclusively in a tooltip. */
export function Tooltip({ content, children, side = 'top', delayDuration = 200 }: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-50 max-w-xs rounded-[8px] bg-text-primary px-2.5 py-1.5 text-helper text-surface-default shadow-overlay animate-fade-in"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-text-primary" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

// ───────── Menu (dropdown) ─────────

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({ className, align = 'end', ...props }: React.ComponentPropsWithoutRef<typeof DropdownMenu.Content>) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={6}
        className={cn('z-50 min-w-48 rounded-menu border border-border-subtle bg-surface-default p-1 shadow-overlay animate-fade-in', className)}
        {...props}
      />
    </DropdownMenu.Portal>
  );
}

export interface MenuItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenu.Item> {
  icon?: React.ReactNode;
  destructive?: boolean;
}

export function MenuItem({ className, icon, destructive, children, ...props }: MenuItemProps) {
  return (
    <DropdownMenu.Item
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded-[8px] px-3 py-2 text-body outline-none [&_svg]:size-4',
        destructive ? 'text-danger data-[highlighted]:bg-danger-bg' : 'text-text-primary data-[highlighted]:bg-brand-tint',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </DropdownMenu.Item>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-border-subtle" />;
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return <DropdownMenu.Label className="px-3 py-1.5 text-helper font-medium text-text-muted">{children}</DropdownMenu.Label>;
}

// ───────── Tabs ─────────

export const Tabs = TabsPrimitive.Root;

export function TabsList({ className, ...props }: React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn('inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-control border border-border-subtle bg-surface-subtle p-1', className)}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[8px] px-3 text-body font-medium text-text-secondary transition-colors',
        'hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
        'data-[state=active]:bg-surface-default data-[state=active]:text-text-primary data-[state=active]:shadow-card',
        'disabled:opacity-50 [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('mt-4 focus-visible:outline-2 focus-visible:outline-focus', className)} {...props} />;
}

// ───────── Dialog ─────────

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Element that opens the dialog (optional for controlled dialogs). */
  trigger?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Prevent closing on outside click (e.g. while submitting). */
  dismissible?: boolean;
}

const DIALOG_WIDTH = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

/** Modal dialog: focus is trapped inside and returns to the trigger on close; Escape closes. */
/** Remembers the element focused when an overlay opened so focus can return to it on close. */
function useReturnFocus(open: boolean | undefined) {
  const ref = React.useRef<HTMLElement | null>(null);
  if (open && !ref.current && typeof document !== 'undefined') ref.current = document.activeElement as HTMLElement | null;
  const onCloseAutoFocus = React.useCallback((e: Event) => {
    const el = ref.current;
    ref.current = null;
    if (el && el.isConnected && el !== document.body) {
      e.preventDefault();
      el.focus();
    }
  }, []);
  return onCloseAutoFocus;
}

export function Dialog({ open, onOpenChange, title, description, children, footer, trigger, size = 'md', dismissible = true }: DialogProps) {
  const onCloseAutoFocus = useReturnFocus(trigger ? undefined : open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in" />
        <DialogPrimitive.Content
          onCloseAutoFocus={trigger ? undefined : onCloseAutoFocus}
          onPointerDownOutside={dismissible ? undefined : (e) => e.preventDefault()}
          onEscapeKeyDown={dismissible ? undefined : (e) => e.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 grid-rows-[auto_1fr_auto]',
            'rounded-dialog border border-border-subtle bg-surface-default shadow-overlay animate-fade-in',
            DIALOG_WIDTH[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-6">
            <div className="grid gap-1">
              <DialogPrimitive.Title className="text-card-title font-semibold text-text-primary">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-text-secondary">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : 'Diálogo'}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="-mr-2 -mt-1 inline-flex size-9 shrink-0 items-center justify-center rounded-control text-text-muted hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus"
            >
              <X aria-hidden className="size-[18px]" />
            </DialogPrimitive.Close>
          </div>
          <div className="overflow-y-auto px-6 py-2">{children}</div>
          {footer ? <div className="flex flex-wrap justify-end gap-2 px-6 pb-6 pt-4">{footer}</div> : <div className="pb-4" />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  /** When set, the user must type this exact text before confirming (deliberate destructive actions). */
  requireText?: string;
  requireTextLabel?: string;
  onConfirm: () => void;
  error?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancelar',
  destructive,
  loading,
  requireText,
  requireTextLabel,
  onConfirm,
  error,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const blocked = requireText !== undefined && typed !== requireText;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      dismissible={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} disabled={blocked} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        {requireText !== undefined ? (
          <FormField label={requireTextLabel ?? `Escribe «${requireText}» para confirmar`}>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </FormField>
        ) : null}
        {error ? (
          <p role="alert" className="text-helper font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ───────── Drawer ─────────

export interface DrawerProps extends Omit<DialogProps, 'size'> {
  side?: 'left' | 'right';
  width?: string;
}

/** Side sheet built on the dialog primitive (focus trapped, Escape closes). */
export function Drawer({ open, onOpenChange, title, description, children, footer, trigger, side = 'right', width = 'w-[min(420px,100vw)]' }: DrawerProps) {
  const onCloseAutoFocus = useReturnFocus(trigger ? undefined : open);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-surface-overlay animate-fade-in" />
        <DialogPrimitive.Content
          onCloseAutoFocus={trigger ? undefined : onCloseAutoFocus}
          className={cn(
            'fixed inset-y-0 z-50 flex flex-col bg-surface-default shadow-overlay',
            side === 'right' ? 'right-0 animate-slide-in-right border-l' : 'left-0 animate-slide-in-left border-r',
            'border-border-subtle',
            width,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
            <div className="grid gap-1">
              <DialogPrimitive.Title className="text-card-title font-semibold text-text-primary">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-body text-text-secondary">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-control text-text-muted hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus"
            >
              <X aria-hidden className="size-[18px]" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto">{children}</div>
          {footer ? <div className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">{footer}</div> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
